import type { PlanarPaneContent } from '@mobile-mapping/web3d';
import type { ImagingApiService } from '@data-access/geospatial/imaging';
import type { MobileMappingApiService } from '@data-access/geospatial/mobile-mapping';
import type { ScanDataApiService } from '@data-access/geospatial/scan-data';
import {
  type ProjectDataState,
  setMissionRunNumberOfStations,
} from '@mobile-mapping/providers';
import { createLoadQueue } from '@trimble/data-loading';
import { createSequentialEffect } from '@trimble/solidjs-utils';
import { detectObjectArrayChanges } from '@trimble/utilities';
import { Accessor } from 'solid-js';
import type { SetStoreFunction } from 'solid-js/store';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import { loadStationImages } from './station-images';
import {
  addCameraStationsToProvider,
  computeStationCountForRun,
} from './stations-processor';
import type { TilesetPrefetchContext } from './tileset-prefetch';
import {
  type PlanarStationLoadContext,
  planarStationLoadContextEquals,
} from './planar-station-load-context';

const STATION_LOAD_CONCURRENCY = 10;

export interface RegisterPlanarStationsLoadingEffectParams {
  readonly loadContext: Accessor<PlanarStationLoadContext>;
  /** Latest planar rows from pane data (for stale-run guard after await). */
  readonly getCurrentPlanarContents: Accessor<PlanarPaneContent[]>;
  readonly getStationsProvider: Accessor<
    StationsCapabilityProvider | undefined
  >;
  readonly setDataState: SetStoreFunction<ProjectDataState>;
  setTilesetPrefetchContext(
    ctx:
      | TilesetPrefetchContext
      | ((prev: TilesetPrefetchContext) => TilesetPrefetchContext)
  ): void;
  readonly imagingApi: ImagingApiService;
  readonly mobileMappingApi: MobileMappingApiService;
  readonly scanDataApi: ScanDataApiService;
  startImagesLoading(): void;
  stopImagesLoading(): void;
}

/**
 * Loads station images (Connect + SDS) when planar pane content or scan region changes.
 */
export function registerPlanarStationsLoadingEffect(
  params: RegisterPlanarStationsLoadingEffectParams
): void {
  const {
    loadContext,
    getCurrentPlanarContents,
    getStationsProvider,
    setDataState,
    setTilesetPrefetchContext,
    imagingApi,
    mobileMappingApi,
    scanDataApi,
    startImagesLoading,
    stopImagesLoading,
  } = params;

  const fetchQueue = createLoadQueue({
    concurrency: STATION_LOAD_CONCURRENCY,
  });

  // Maps a run to the token of its latest scheduling. A task only releases its
  // own entry (matching token), so a stale aborted task cannot delete the entry
  // belonging to a newer re-scheduled task for the same run.
  const scheduledRuns = new Map<string, symbol>();

  const runAbortControllers = new Map<string, AbortController>();

  let activeTasks = 0;
  const beginTask = (): void => {
    if (activeTasks === 0) {
      startImagesLoading();
    }
    activeTasks++;
  };
  const endTask = (): void => {
    activeTasks--;
    if (activeTasks === 0) {
      stopImagesLoading();
    }
  };

  createSequentialEffect(
    loadContext,
    async (ctx, prevCtx) => {
      const stationsProvider = getStationsProvider();
      if (!stationsProvider) {
        return;
      }

      const currentPlanarContents = ctx.planarContents;
      const prevPlanarContents = prevCtx?.planarContents ?? [];

      const { newItems, removedItems } = detectObjectArrayChanges(
        prevPlanarContents,
        currentPlanarContents,
        'selectedRunId'
      );

      if (removedItems.length > 0) {
        const removedRunIds = removedItems.map((c) => c.selectedRunId);
        stationsProvider.removeAllStationsForRuns(removedRunIds);
        stationsProvider.clearCacheForRemovedCameras();
        removedItems.forEach((content) => {
          scheduledRuns.delete(content.selectedRunId);
          runAbortControllers.get(content.selectedRunId)?.abort();
          runAbortControllers.delete(content.selectedRunId);
          setMissionRunNumberOfStations(
            setDataState,
            content.selectedMissionTrn,
            content.selectedRunId,
            undefined
          );
        });
      }

      const regionResolvedOrChanged =
        ctx.tcRegion !== undefined &&
        (prevCtx?.tcRegion === undefined ||
          prevCtx.tcRegion.location !== ctx.tcRegion.location);

      const candidates =
        regionResolvedOrChanged && currentPlanarContents.length > 0
          ? currentPlanarContents
          : newItems;
      if (regionResolvedOrChanged) {
        candidates.forEach((c) => {
          scheduledRuns.delete(c.selectedRunId);
          runAbortControllers.get(c.selectedRunId)?.abort();
          runAbortControllers.delete(c.selectedRunId);
        });
      }

      const itemsToLoad = candidates.filter(
        (content) => !scheduledRuns.has(content.selectedRunId)
      );

      for (const content of itemsToLoad) {
        const scheduleToken = Symbol(content.selectedRunId);
        scheduledRuns.set(content.selectedRunId, scheduleToken);
        beginTask();
        fetchQueue.enqueue(
          () => loadRun(content, ctx, scheduleToken).finally(endTask),
          content.selectedRunId
        );
      }
    },
    { equals: planarStationLoadContextEquals }
  );

  async function loadRun(
    content: PlanarPaneContent,
    ctx: PlanarStationLoadContext,
    scheduleToken: symbol
  ): Promise<void> {
    const runId = content.selectedRunId;

    // Only clear the scheduling record if this task still owns it; a newer
    // re-scheduled task may have replaced the entry while this one was running.
    const releaseSchedule = (): void => {
      if (scheduledRuns.get(runId) === scheduleToken) {
        scheduledRuns.delete(runId);
      }
    };

    const isRunStillPresent = (): boolean =>
      getCurrentPlanarContents().some(
        (pc) =>
          pc.selectedMissionTrn === content.selectedMissionTrn &&
          pc.selectedRunId === content.selectedRunId
      );

    // Guards against stale queued tasks: a task enqueued for a prior region may
    // still be pending (no abort controller yet) when the region changes, so
    // the region-change abort is a no-op for it. Bail here instead of loading
    // stations for the wrong region.
    const isRegionStale = (): boolean =>
      loadContext().tcRegion?.location !== ctx.tcRegion?.location;

    if (!isRunStillPresent() || isRegionStale()) {
      releaseSchedule();
      return;
    }

    const abortController = new AbortController();
    runAbortControllers.set(runId, abortController);

    try {
      const stationsList = await loadStationImages(
        content,
        imagingApi,
        mobileMappingApi,
        {
          scanDataApi,
          region: ctx.tcRegion ?? undefined,
          regions: ctx.regions,
          signal: abortController.signal,
        }
      );

      const stationsProvider = getStationsProvider();
      if (
        !stationsProvider ||
        abortController.signal.aborted ||
        !isRunStillPresent() ||
        isRegionStale()
      ) {
        releaseSchedule();
        return;
      }

      addCameraStationsToProvider(stationsList, stationsProvider);

      const stationCount = computeStationCountForRun(stationsList);

      setMissionRunNumberOfStations(
        setDataState,
        content.selectedMissionTrn,
        content.selectedRunId,
        stationCount
      );

      setTilesetPrefetchContext((prev) => {
        const next = { ...prev };
        stationsList.forEach((vs) => {
          if (!vs.datasetTrn) {
            return;
          }
          const runEntry = next[vs.runId] ?? {};
          runEntry[vs.name] = {
            tenantTrn: content.selectedTenantTrn,
            datasetTrn: vs.datasetTrn,
          };
          next[vs.runId] = runEntry;
        });
        return next;
      });
    } catch (error) {
      releaseSchedule();
      if (abortController.signal.aborted) {
        return;
      }
      throw error;
    } finally {
      if (runAbortControllers.get(runId) === abortController) {
        runAbortControllers.delete(runId);
      }
    }
  }
}
